import { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import i18nContext from 'components/i18n-context';
import CustomFileDragAndDrop from 'components/CustomFileDragAndDrop';
import Input from 'uikit/Input/Input';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import Button from 'uikit/Button/Button';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import { toast } from 'react-toastify';
import { noCyrillicRegExp } from 'services/utils';
import './AttachDocumentModal.scss';
import Loader from '../../../../../../../../components/Loader';

const DOCUMENT_CATEGORIES = [
  { key: 'INVOICE', value: 'transactionDetails.documentRequest.type.invoice' },
  { key: 'AGREEMENT', value: 'transactionDetails.documentRequest.type.agreement' },
  { key: 'TRANSPORTATION_DOCUMENTS', value: 'transactionDetails.documentRequest.type.transportationDocuments' },
  { key: 'PROOF_OF_WORK', value: 'transactionDetails.documentRequest.type.proofOfWork' },
  { key: 'BANK_STATEMENT', value: 'transactionDetails.documentRequest.type.bankStatement' },
  { key: 'TAX_DECLARATION', value: 'transactionDetails.documentRequest.type.taxDeclaration' },
  { key: 'IDENTIFICATION_DOCUMENT', value: 'transactionDetails.documentRequest.type.identificationDocument' },
  { key: 'PROOF_OF_ADDRESS', value: 'transactionDetails.documentRequest.type.proofOfAddress' },
  { key: 'PROOF_OF_DIVIDENDS', value: 'transactionDetails.documentRequest.type.proofOfDividends' },
  { key: 'PURPOSE_OF_PAYMENT', value: 'transactionDetails.documentRequest.type.purposeOfPayment' },
  { key: 'OTHER', value: 'transactionDetails.documentRequest.type.other' }
];

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB in bytes
const ALLOWED_FORMATS = ['pdf', 'jpg', 'jpeg', 'png'];
const MAX_NOTE_LENGTH = 2000;

const AttachDocumentModal = ({ isOpen, onClose, onUpload, isLoading, editingDocument, onDownload }) => {
  const i18n = useContext(i18nContext);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [category, setCategory] = useState('');
  const [isHidden, setIsHidden] = useState(true);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState({});

  // Populate form with editing document data
  useEffect(() => {
    if (editingDocument && isOpen) {
      setCategory(editingDocument.category || '');
      setIsHidden(editingDocument.hidden !== undefined ? editingDocument.hidden : true);
      setNote(editingDocument.note || '');

      // Create a file object for preview WITHOUT loading blob from backend
      if (editingDocument.name && editingDocument.size) {
        // Create a simple object that CustomFileDragAndDrop can display
        const fileWithPreview = {
          id: editingDocument.id || `doc-${Date.now()}`,
          name: editingDocument.name,
          size: editingDocument.size,
          type: editingDocument.contentType || 'application/octet-stream',
          // Add a special handler for download that will be used in CustomFileDragAndDrop
          onDownloadClick: () => {
            if (onDownload && editingDocument.id) {
              onDownload(editingDocument.id);
            }
          }
        };
        setSelectedFiles([fileWithPreview]);
      } else {
        setSelectedFiles([]);
      }
    } else if (!isOpen) {
      // Reset form when modal closes
      setSelectedFiles([]);
      setCategory('');
      setIsHidden(true);
      setNote('');
      setErrors({});
    }
  }, [editingDocument, isOpen]);

  if (!isOpen) return null;

  const validateFile = (file) => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(i18n.getMessage('transactionDetails.documents.maxSizeError'));
      return false;
    }

    // Check file format
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_FORMATS.includes(fileExtension)) {
      toast.error(i18n.getMessage('transactionDetails.documents.invalidFormatError'));
      return false;
    }

    return true;
  };

  // Handler for file selection from CustomFileDragAndDrop
  const handleFileChange = ({ target: { files } }) => {
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      const validFiles = newFiles.filter(validateFile);
      if (validFiles.length > 0) {
        // Add unique id to each file for tracking
        const filesWithIds = validFiles.map((file) => {
          // Create a new File object with id property
          const fileWithId = new File([file], file.name, { type: file.type });
          fileWithId.id = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return fileWithId;
        });
        setSelectedFiles((prevFiles) => [...prevFiles, ...filesWithIds]);
        setErrors((prev) => ({ ...prev, files: null }));
      }
    }
  };

  // Handler for removing files from CustomFileDragAndDrop
  const handleRemoveFiles = (fileIdToRemove) => {
    // Don't allow removing the original file when editing
    if (editingDocument && fileIdToRemove === editingDocument.id) {
      toast.error(i18n.getMessage('transactionDetails.documents.cannotRemoveOriginal'));
      return;
    }
    setSelectedFiles((prev) =>
      prev.filter((file) => {
        // Compare by file.id which we ensure exists when adding files
        return file.id !== fileIdToRemove;
      })
    );
  };

  const handleSubmit = async () => {
    const newErrors = {};

    // Only require files when creating new document, not when editing
    if (!editingDocument && selectedFiles.length === 0) {
      newErrors.files = i18n.getMessage('transactionDetails.addDocument.modal.error.document.empty');
    }

    if (!category) {
      newErrors.category = i18n.getMessage('transactionDetails.addDocument.modal.error.category.empty');
    }

    // Validate note for Cyrillic characters
    if (note && !noCyrillicRegExp.test(note)) {
      newErrors.note = i18n.getMessage('validation.noCyrillic') || 'Cyrillic characters are not allowed';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onUpload({
        files: selectedFiles,
        category,
        isHidden,
        note
      });

      // Reset form and close modal
      setSelectedFiles([]);
      setCategory('');
      setIsHidden(true);
      setNote('');
      setErrors({});
      onClose();
    } catch (error) {
      toast.error(
        editingDocument
          ? i18n.getMessage('transactionDetails.documents.updateError')
          : i18n.getMessage('transactionDetails.documents.uploadError')
      );
    }
  };

  const categoryOptions = DOCUMENT_CATEGORIES.map((cat) => ({
    key: cat.key,
    value: i18n.getMessage(cat.value)
  }));

  const handleNoteChange = (e) => {
    const value = e.target.value;
    setNote(value);

    // Check for Cyrillic characters
    if (value && !noCyrillicRegExp.test(value)) {
      setErrors((prev) => ({
        ...prev,
        note: i18n.getMessage('validation.noCyrillic') || 'Cyrillic characters are not allowed'
      }));
    } else {
      setErrors((prev) => ({ ...prev, note: null }));
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    setCategory('');
    setIsHidden(true);
    setNote('');
    setErrors({});
    onClose();
  };

  return (
    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    <div className='attach-document-modal__overlay' onClick={handleCancel}>
      <div className='attach-document-modal' onClick={(e) => e.stopPropagation()}>
        <div className='attach-document-modal__header'>
          <h2 className='attach-document-modal__title'>
            {editingDocument
              ? i18n.getMessage('transactionDetails.documents.editDocument')
              : i18n.getMessage('transactionDetails.documents.attachDocument')}
          </h2>
          <CloseIcon className={'attach-document-modal__close'} onClick={handleCancel} />
        </div>

        <div className='attach-document-modal__body'>
          {/* File Upload Area */}
          <div className='attach-document-modal__dropzone'>
            <CustomFileDragAndDrop
              files={selectedFiles}
              onChange={handleFileChange}
              onRemoveFiles={handleRemoveFiles}
              maxSize={MAX_FILE_SIZE}
              accept='.pdf,.jpg,.jpeg,.png'
              error={errors.files}
              isDisabled={isLoading || !!editingDocument}
            />
            {editingDocument && (
              <p style={{ marginTop: '8px', color: '#6B7280', fontSize: '14px', textAlign: 'center' }}>
                {i18n.getMessage('transactionDetails.documents.editModeFileInfo')}
              </p>
            )}
          </div>

          {/* Category Dropdown */}
          <div className='attach-document-modal__field'>
            <InputDropDownSearch
              isRequired
              label={i18n.getMessage('transactionDetails.documents.documentCategory')}
              placeholder={i18n.getMessage('transactionDetails.documents.selectCategory')}
              id='document-category'
              name='category'
              value={category}
              options={categoryOptions}
              onChange={(name, value) => {
                setCategory(value);
                setErrors((prev) => ({ ...prev, category: null }));
              }}
              error={errors.category}
            />
          </div>

          {/* Hidden Checkbox */}
          <div className='attach-document-modal__checkbox'>
            <CheckBox
              className='document-checkbox'
              isActive={isHidden}
              isReverseView={true}
              showCheckBox={true}
              onChange={() => setIsHidden(!isHidden)}
              value={i18n.getMessage('transactionDetails.documents.hidden')}
            />
          </div>

          {/* Note Textarea */}
          <div className='attach-document-modal__textarea'>
            <Input
              type='textarea'
              rows={3}
              label={i18n.getMessage('transactionDetails.documents.note')}
              placeholder={i18n.getMessage('transactionDetails.documents.addNote')}
              id='document-note'
              name='note'
              value={note}
              onChange={handleNoteChange}
              max={MAX_NOTE_LENGTH}
              subText={`${note.length}/${MAX_NOTE_LENGTH}`}
              error={errors.note}
            />
          </div>

          {/* Action Buttons */}
          <div className='attach-document-modal__actions'>
            <Button type='secondary' size='large' onClick={handleCancel} isDisabled={isLoading}>
              {i18n.getMessage('transactionDetails.documents.cancel')}
            </Button>
            <Button
              type='primary'
              size='large'
              onClick={handleSubmit}
              isDisabled={isLoading || (!editingDocument && selectedFiles.length === 0)}
            >
              {isLoading ? (
                <Loader />
              ) : editingDocument ? (
                i18n.getMessage('transactionDetails.documents.updateDocument')
              ) : (
                i18n.getMessage('transactionDetails.documents.attachDocument')
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
    /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  );
};

AttachDocumentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  editingDocument: PropTypes.object,
  onDownload: PropTypes.func
};

export default AttachDocumentModal;
